import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { MenuItem } from 'primeng/api';
import { Subject, takeUntil } from 'rxjs';
import { HasPermissionDirective } from '../../../../../directives/has-permission.directive';
import { accessRightAssetDefinition } from '../../../../../entity-definitions/access-right.asset.definition';
import { AuthorizationService } from '../../../../../services/authorization.service';
import { EntityDialogService } from '../../../../../services/entity-dialog.service';
import { ToastService } from '../../../../../services/toast.service';
import { AccessRightDomainStore, AccessRightStore } from '../../../../../stores/access-right.store';
import { AssetStore } from '../../../../../stores/asset.store';
import { RoleStore } from '../../../../../stores/role.store';
import { UserStore } from '../../../../../stores/user.store';
import { AccessRight, AccessRightDomain } from '../../../../../types/access-right.interface';
import { Asset } from '../../../../../types/asset.interface';
import { Role } from '../../../../../types/role.interface';
import { User } from '../../../../../types/user.interface';
import { GenericParentGridComponent } from '../../../../generic/generic-parent-grid.component';
import { CesAssetBreadcrumbComponent } from '../../../../ui/ces-asset-breadcrumb/ces-asset-breadcrumb.component';
import { CesButtonComponent } from '../../../../ui/ces-buttons';
import { CesTitleComponent } from '../../../../ui/ces-route-title/ces-route-title.component';

interface AssetStoreApi {
  byId(): (id: number) => Asset | undefined;
  loadById(id: number): Promise<Asset>;
}

interface UserStoreApi {
  entities(): User[];
  loadAll(): Promise<User[]>;
  byId(): (id: number) => User | undefined;
  assignableUsers(): User[];
}

interface RoleStoreApi {
  entities(): Role[];
  sortedByName(): Role[];
  loadAll(): Promise<Role[]>;
}

interface AccessRightDomainStoreApi {
  entities(): AccessRightDomain[];
  loadByAssetId(assetId: number): Promise<AccessRightDomain[]>;
}

interface AccessRightStoreApi {
  create(entity: Partial<AccessRight>): Promise<AccessRight | null>;
  update(id: number, changes: Partial<AccessRight>): Promise<AccessRight | null>;
  delete(id: number): Promise<void>;
}

@Component({
  selector: 'access-right-by-asset-list',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CesAssetBreadcrumbComponent,
    CesTitleComponent,
    GenericParentGridComponent,
    CesButtonComponent,
    HasPermissionDirective,
    TranslocoModule,
  ],
  templateUrl: './access-right-by-asset-list.component.html',
})
export class AccessRightByAssetListComponent implements OnInit, OnDestroy {
  protected readonly userStore = inject(UserStore) as unknown as UserStoreApi;
  protected readonly assetStore = inject(AssetStore) as unknown as AssetStoreApi;
  protected readonly roleStore = inject(RoleStore) as unknown as RoleStoreApi;
  protected readonly accessRightDomainStore = inject(AccessRightDomainStore) as unknown as AccessRightDomainStoreApi;
  protected readonly accessRightStore = inject(AccessRightStore) as unknown as AccessRightStoreApi;
  private readonly route = inject(ActivatedRoute);
  private readonly dialogService = inject(EntityDialogService);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly translateService = inject(TranslocoService);
  private readonly toastService = inject(ToastService);

  readonly accessRightDefinition = accessRightAssetDefinition;
  accessRightsDomain: AccessRightDomain[] = [];
  users: User[] = [];
  roles: Role[] = [];
  readonly currentAssetId$ = signal<number | null>(null);
  currentAsset: Asset | null = null;

  private readonly translations = toSignal(this.translateService.selectTranslation());

  readonly breadcrumbTrail = computed<MenuItem[]>(() => {
    this.translations();
    return [{ label: this.translateService.translate('navigation.routes.accessRights') }];
  });

  get currentAssetId(): number | null {
    return this.currentAssetId$();
  }
  set currentAssetId(value: number | null) {
    this.currentAssetId$.set(value);
  }

  readonly entityDefinitionWithPermissions = computed(() => {
    const canAccessConfig = this.authorizationService.canAccessAdminFeatures();
    const userById = this.userStore.byId();
    const assetId = this.currentAssetId$() ?? undefined;
    return {
      ...this.accessRightDefinition,
      actionButtons: {
        ...this.accessRightDefinition.actionButtons,
        parent: {
          ...this.accessRightDefinition.actionButtons?.parent,
          addPermission: { method: 'postAccessRightListResource' as const, assetId },
          editPermission: { method: 'putAccessRightDetailResource' as const, assetId },
          deletePermission: { method: 'deleteAccessRightDetailResource' as const, assetId },
        },
      },
      rowLinkHref: (entity: AccessRightDomain) => {
        if (!canAccessConfig) return null;
        const user = userById(entity.userId);
        return user ? `/configuration/access-rights/users/${user.id}` : null;
      },
    };
  });

  readonly tablePermissionConfig = computed(() => ({
    method: 'putAccessRightDetailResource' as const,
    type: 'read' as const,
    assetId: this.currentAssetId$() ?? undefined,
  }));

  private readonly destroy$ = new Subject<void>();
  dataLoadingCompleted = false;

  ngOnInit(): void {
    this.route.parent?.parent?.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
      if (params['id']) {
        this.currentAssetId = Number(params['id']);
        if (this.currentAssetId > 0) {
          void this.loadAssetDetails(this.currentAssetId);
          void this.loadReferenceData();
        } else {
          console.error(this.translateService.translate('common.errors.invalidAssetId'), this.currentAssetId);
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async loadAssetDetails(assetId: number): Promise<void> {
    try {
      this.currentAsset = this.assetStore.byId()(assetId) ?? null;

      if (!this.currentAsset) {
        await this.assetStore.loadById(assetId);
        this.currentAsset = this.assetStore.byId()(assetId) ?? null;
      }
    } catch (error) {
      console.error(this.translateService.translate('common.errors.assetLoadFailed'), assetId, error);
    }
  }

  private async loadReferenceData(): Promise<void> {
    if (!this.currentAssetId) return;

    try {
      await Promise.all([this.userStore.loadAll(), this.roleStore.loadAll()]);
      this.users = this.userStore.assignableUsers();
      this.roles = this.roleStore.sortedByName();
      await this.loadAccessRightsData();
    } catch (error) {
      console.error(this.translateService.translate('common.errors.referenceDataLoadFailed'), error);
    }
  }

  private async loadAccessRightsData(): Promise<void> {
    try {
      if (this.currentAssetId) {
        await this.accessRightDomainStore.loadByAssetId(this.currentAssetId);

        this.accessRightsDomain = this.accessRightDomainStore.entities();

        this.dataLoadingCompleted = true;
      }
    } catch (error) {
      console.error(this.translateService.translate('common.errors.dataLoadFailed'), error);
    } finally {
      this.cdr.markForCheck();
    }
  }

  createAccessRight(): void {
    if (!this.currentAssetId || !this.currentAsset) {
      console.error(this.translateService.translate('common.errors.noAssetSelected'));
      return;
    }

    const targetAssetId = this.currentAssetId;
    const targetAsset = this.currentAsset;

    this.dialogService
      .openCreateDialog<AccessRightDomain>({
        header: this.accessRightDefinition.messages.dialogTitles.create(),
        headerParams: { name: targetAsset.name },
        entityDefinition: this.accessRightDefinition,
        mode: 'create',
        additionalData: {
          Origin: 'asset',
          Asset: [targetAsset],
          Role: this.roles,
          User: this.users,
          AccessRights: this.accessRightsDomain,
        },
      })
      .subscribe(formData => {
        if (!formData) return;
        if ('__action' in formData && formData.__action === 'delete') return;

        const payload: Partial<AccessRight> = {
          ...(formData as Partial<AccessRight>),
          assetId: targetAssetId,
        };

        void this.accessRightStore.create(payload).then(() => {
          void this.loadAccessRightsData();
        });
      });
  }

  editAccessRight(accessright: AccessRightDomain): void {
    if (!accessright.assetId || accessright.assetId <= 0) {
      console.error(this.translateService.translate('common.errors.invalidAssetIdOnAccessRight'), accessright);
      return;
    }

    const user = this.userStore.byId()(accessright.userId);

    this.dialogService
      .openEditDialog<AccessRightDomain>(accessright, {
        header: this.accessRightDefinition.messages.dialogTitles.edit(),
        headerParams: { name: user?.displayName ?? '' },
        entityDefinition: this.accessRightDefinition,
        mode: 'edit',
        additionalData: {
          Origin: 'asset',
          Asset: this.currentAsset ? [this.currentAsset] : [],
          Role: this.roles,
          User: this.users,
          AccessRights: this.accessRightsDomain,
        },
        onDelete: entity => this.deleteAccessRight(entity),
      })
      .subscribe(result => {
        if (!result) return;
        if ('__action' in result && result.__action === 'delete') {
          this.deleteAccessRight(result.entity as AccessRightDomain);
          return;
        }

        const payload: Partial<AccessRight> = {
          ...(result as Partial<AccessRight>),
          assetId: accessright.assetId,
        };

        void this.accessRightStore.update(accessright.id, payload).then(() => {
          void this.loadAccessRightsData();
        });
      });
  }

  deleteAccessRight(accessRight: AccessRightDomain): void {
    if (!accessRight?.id) {
      console.error(this.translateService.translate('common.errors.invalidAccessRightDeletion'));
      return;
    }

    this.accessRightStore
      .delete(accessRight.id)
      .then(() => {
        void this.loadAccessRightsData();
      })
      .catch((error: unknown) => {
        this.toastService.warning('entities.access.messages.deleteError');
        console.error(this.translateService.translate('common.errors.accessRightDeletionFailed'), error);
      });
  }
}
